import { cn } from "@/lib/utils";
import { ClickableRow } from "@/components/shared/ClickableRow";
import { formatNOK, formatHours } from "@/lib/format";
import type { ReportColumn, ReportRow } from "@/lib/reports/types";

function fmt(v: number | string, kind?: ReportColumn["kind"]): string {
  if (typeof v === "string") return v;
  if (kind === "kr") return formatNOK(v);
  if (kind === "timer") return formatHours(v);
  if (kind === "pct") return `${Math.round(v)}%`;
  return v.toLocaleString("nb-NO");
}

export function ReportTable({
  columns, rows, onDrill, summary,
}: { columns: ReportColumn[]; rows: ReportRow[]; onDrill: (id: string) => void; summary?: ReportRow | null }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b text-[11px] uppercase tracking-wider text-muted-foreground">
          {columns.map((c) => (
            <th key={c.key} className={cn("py-2 px-3", c.align === "right" && "text-right", c.align === "center" && "text-center", (!c.align || c.align === "left") && "text-left")}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <ClickableRow
            key={r.id}
            data-row-id={r.id}
            disabled={!r.canDrill}
            onActivate={() => onDrill(r.id)}
            className={cn("border-b border-border/50 last:border-0", r.canDrill && "hover:bg-muted/30")}
          >
            {columns.map((c) => (
              <td key={c.key} className={cn("py-2 px-3 tabular-nums", c.align === "right" && "text-right", c.align === "center" && "text-center")}>
                {fmt(c.key === "label" ? r.label : r.cells[c.key] ?? "—", c.kind)}
              </td>
            ))}
          </ClickableRow>
        ))}
        {rows.length === 0 && (
          <tr><td colSpan={columns.length} className="py-8 text-center text-muted-foreground">Ingen data</td></tr>
        )}
        {summary && rows.length > 0 && (
          <tr data-row-id={summary.id} className="border-t-2 border-foreground font-semibold">
            {columns.map((c) => (
              <td key={c.key} className={cn("py-2 px-3 tabular-nums", c.align === "right" && "text-right", c.align === "center" && "text-center")}>
                {c.key === "label" ? summary.label : c.key in summary.cells ? fmt(summary.cells[c.key], c.kind) : ""}
              </td>
            ))}
          </tr>
        )}
      </tbody>
    </table>
  );
}
